/**
 * usePreviewOffer - Lightweight offer preview without checkout sessions
 * 
 * This hook provides a fast, client-side offer preview by:
 * 1. Fetching offer data with pre-computed summaries
 * 2. Allowing variant/quantity selection
 * 3. Recalculating totals on-the-fly without backend calls
 * 4. NO checkout session creation - perfect for browsing/previewing
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Offer, OffersResource } from '../../core/resources/offers';
import { getGlobalApiClient } from './useApiQuery';

export interface PreviewLineItemSelection {
    lineItemId: string;
    variantId: string;
    quantity: number;
    priceId?: string;
}

export interface PreviewOfferSummary {
    items: Array<{
        id: string;
        productId: string;
        variantId: string;
        priceId: string;
        productName: string;
        variantName: string;
        imageUrl: string | null;
        quantity: number;
        unitAmount: number;
        amount: number;
        adjustedAmount: number;
        currency: string;
    }>;
    totalAmount: number;
    totalAdjustedAmount: number;
    totalPromotionAmount: number;
    currency: string;
    options: Record<string, Array<{
        variantId: string;
        variantName: string;
        sku: string | null;
        imageUrl: string | null;
        prices: Array<{
            id: string;
            currencyOptions: Record<string, { amount: number; currency: string }>;
        }>;
        default: boolean;
    }>>;
}

export interface UsePreviewOfferOptions {
    offerId: string;
    currency?: string;
    initialSelections?: Record<string, PreviewLineItemSelection>;
}

export interface UsePreviewOfferResult {
    offer: Offer | null;
    isLoading: boolean;
    isFetching: boolean;
    isPaying: boolean;
    error: Error | null;
    summary: PreviewOfferSummary | null;
    selections: Record<string, PreviewLineItemSelection>;
    // Precise methods (by lineItemId)
    selectVariant: (lineItemId: string, variantId: string) => void;
    updateQuantity: (lineItemId: string, quantity: number) => void;
    // Simple methods (by productId - affects all items with this product)
    selectVariantByProduct: (productId: string, variantId: string) => void;
    updateQuantityByProduct: (productId: string, quantity: number) => void;
    getAvailableVariants: (productId: string) => Array<{
        variantId: string;
        variantName: string;
        imageUrl: string | null;
        unitAmount: number;
        currency: string;
    }>;
    // Payment
    pay: (mainOrderId?: string) => Promise<{ checkoutUrl: string }>;
    toCheckout: (mainOrderId?: string) => Promise<{ checkoutToken: string; customerId: string; status: 'processing' }>;
}

export function usePreviewOffer(options: UsePreviewOfferOptions): UsePreviewOfferResult {
    const { offerId, currency: requestedCurrency = '', initialSelections = {} } = options;
    const queryParamsCurrency =
        typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search).get('currency') || undefined
            : undefined;
    const effectiveCurrency = queryParamsCurrency || requestedCurrency;
    const offersResource = useMemo(() => new OffersResource(getGlobalApiClient()), []);

    // Track user selections (variant + quantity per product)
    const [selections, setSelections] = useState<Record<string, PreviewLineItemSelection>>(initialSelections);
    const [isPaying, setIsPaying] = useState(false);

    // Build lineItems array for API call
    const lineItemsForPreview = useMemo(() => {
        if (Object.keys(selections).length === 0) return undefined;

        return Object.values(selections).map((selection) => {
            const item: any = {
                variantId: selection.variantId,
                quantity: selection.quantity,
            };
            // Include lineItemId for precise matching
            if (selection.lineItemId) {
                item.lineItemId = selection.lineItemId;
            }
            return item;
        });
    }, [selections]);

    // 🎯 ONE API CALL - Fetch offer + preview in one request
    const { data, isLoading, isFetching, error } = useQuery({
        queryKey: ['offer-preview', offerId, effectiveCurrency, lineItemsForPreview],
        queryFn: async () => {
            console.log('🔍 [usePreviewOffer] Fetching preview:', {
                offerId,
                currency: effectiveCurrency,
                lineItems: lineItemsForPreview,
            });

            const result = await offersResource.previewOffer(offerId, effectiveCurrency, lineItemsForPreview);

            console.log('✅ [usePreviewOffer] Preview result:', result);

            // Extract offer and preview from unified response
            const offer = result.offer || null;
            const preview = result.preview || result; // Backwards compatibility

            // Transform preview to expected format
            const summary = {
                items: (preview.items || []).map((item: any) => ({
                    id: item.id,
                    productId: item.productId,
                    variantId: item.variantId,
                    priceId: item.priceId,
                    productName: item.product?.name || item.orderLineItemProduct?.name || '',
                    variantName: item.variant?.name || item.orderLineItemVariant?.name || '',
                    imageUrl: item.variant?.imageUrl || item.orderLineItemVariant?.imageUrl || null,
                    quantity: item.quantity,
                    unitAmount: item.unitAmount,
                    amount: item.amount,
                    adjustedAmount: item.adjustedAmount,
                    currency: item.currency,
                })),
                totalAmount: preview.totalAmount || 0,
                totalAdjustedAmount: preview.totalAdjustedAmount || 0,
                totalPromotionAmount: preview.totalPromotionAmount || 0,
                currency: preview.currency || effectiveCurrency,
                options: preview.options || {},
            };

            return { offer, summary };
        },
        enabled: !!offerId,
        staleTime: 0, // Always refetch, no cache
        gcTime: 5000, // Keep in cache for 5s during transitions
        placeholderData: (previousData) => previousData, // Keep previous data during refetch
        refetchOnMount: 'always', // Always refetch on mount
        retry: false, // Don't retry on error
        retryOnMount: false,
    });

    const offer = data?.offer || null;
    const summary = data?.summary || null;

    // Initialize selections from preview response on first load
    useEffect(() => {
        if (summary && Object.keys(selections).length === 0) {
            const initialSelections: Record<string, PreviewLineItemSelection> = {};
            summary.items.forEach((item: any) => {
                // Use lineItemId as key to support same product with different variants
                initialSelections[item.id] = {
                    lineItemId: item.id,
                    variantId: item.variantId,
                    quantity: item.quantity,
                    priceId: item.priceId,
                };
            });
            console.log('🎬 [usePreviewOffer] Initialized selections:', initialSelections);
            setSelections(initialSelections);
        }
    }, [summary, selections]);

    console.log('🎁 [usePreviewOffer] State:', {
        offerId,
        isLoading,
        isFetching,
        hasData: !!data,
        hasOffer: !!offer,
        hasSummary: !!summary,
        selectionsCount: Object.keys(selections).length,
        error: error?.message,
    });

    // ===== Precise methods (by lineItemId) =====

    // Select a variant for a specific line item
    const selectVariant = useCallback((lineItemId: string, variantId: string) => {
        const item = summary?.items.find((i: any) => i.id === lineItemId);
        if (!item) return;

        const variants = summary?.options?.[item.productId];
        if (!variants) return;

        const selectedVariant = variants.find((v: any) => (v.id || v.variantId) === variantId);
        if (!selectedVariant) return;

        const priceId = selectedVariant.prices?.[0]?.id;

        setSelections(prev => ({
            ...prev,
            [lineItemId]: {
                lineItemId,
                variantId,
                quantity: prev[lineItemId]?.quantity || item.quantity,
                priceId,
            },
        }));
    }, [summary]);

    // Update quantity for a specific line item
    const updateQuantity = useCallback((lineItemId: string, quantity: number) => {
        if (quantity < 1) return;

        const item = summary?.items.find((i: any) => i.id === lineItemId);
        if (!item) return;

        setSelections(prev => ({
            ...prev,
            [lineItemId]: {
                lineItemId,
                variantId: prev[lineItemId]?.variantId || item.variantId,
                quantity,
                priceId: prev[lineItemId]?.priceId || item.priceId,
            },
        }));
    }, [summary]);

    // ===== Simple methods (by productId - affects all items with this product) =====

    // Select a variant for all line items with this product
    const selectVariantByProduct = useCallback((productId: string, variantId: string) => {
        if (!summary?.options?.[productId]) return;

        const variants = summary.options[productId];
        const selectedVariant = variants.find((v: any) => (v.id || v.variantId) === variantId);
        if (!selectedVariant) return;

        const priceId = selectedVariant.prices?.[0]?.id;

        setSelections(prev => {
            const updated = { ...prev };
            // Update all line items with this productId
            summary.items.forEach((item: any) => {
                if (item.productId === productId) {
                    updated[item.id] = {
                        lineItemId: item.id,
                        variantId,
                        quantity: prev[item.id]?.quantity || item.quantity,
                        priceId,
                    };
                }
            });
            return updated;
        });
    }, [summary]);

    // Update quantity for all line items with this product
    const updateQuantityByProduct = useCallback((productId: string, quantity: number) => {
        if (quantity < 1) return;

        setSelections(prev => {
            const updated = { ...prev };
            // Update all line items with this productId
            summary?.items.forEach((item: any) => {
                if (item.productId === productId) {
                    updated[item.id] = {
                        lineItemId: item.id,
                        variantId: prev[item.id]?.variantId || item.variantId,
                        quantity,
                        priceId: prev[item.id]?.priceId || item.priceId,
                    };
                }
            });
            return updated;
        });
    }, [summary]);

    // Get available variants for a product
    const getAvailableVariants = useCallback((productId: string) => {
        if (!summary?.options?.[productId]) return [];

        const currentItem = summary.items.find((i: any) => i.productId === productId);
        const activePriceId = currentItem?.priceId;
        const currency = summary.currency || effectiveCurrency;

        return summary.options[productId].map((variant: any) => {
            // Find matching price or use first
            let unitAmount = 0;
            if (activePriceId && variant.prices) {
                const matchingPrice = variant.prices.find((p: any) => p.id === activePriceId);
                if (matchingPrice?.currencyOptions?.[currency]) {
                    unitAmount = matchingPrice.currencyOptions[currency].amount;
                }
            }

            // Fallback to first price
            if (unitAmount === 0 && variant.prices?.[0]?.currencyOptions?.[currency]) {
                unitAmount = variant.prices[0].currencyOptions[currency].amount;
            }

            return {
                variantId: variant.id || variant.variantId,
                variantName: variant.name || variant.variantName,
                imageUrl: variant.imageUrl,
                unitAmount,
                currency,
            };
        });
    }, [summary, effectiveCurrency]);

    // Pay for the offer with current selections
    const pay = useCallback(async (mainOrderId?: string): Promise<{ checkoutUrl: string }> => {
        if (isPaying) {
            throw new Error('Payment already in progress');
        }

        setIsPaying(true);

        try {
            const result = await offersResource.payPreviewedOffer(
                offerId,
                effectiveCurrency,
                lineItemsForPreview,
                typeof window !== 'undefined' ? window.location.href : undefined,
                mainOrderId
            );

            console.log('[usePreviewOffer] Payment initiated:', result);

            return {
                checkoutUrl: result.checkout.checkoutUrl,
            };
        } catch (error) {
            console.error('[usePreviewOffer] Payment failed:', error);
            throw error;
        } finally {
            setIsPaying(false);
        }
    }, [offerId, effectiveCurrency, lineItemsForPreview, offersResource, isPaying]);

    // Create checkout session without paying (for landing pages) - async mode for fast response
    const toCheckout = useCallback(async (mainOrderId?: string): Promise<{ checkoutToken: string; customerId: string; status: 'processing' }> => {
        if (isPaying) {
            throw new Error('Operation already in progress');
        }

        setIsPaying(true);

        try {
            // Use async mode for fast response (~50ms vs 2-5s)
            const result = await offersResource.toCheckoutAsync(
                offerId,
                effectiveCurrency,
                lineItemsForPreview,
                typeof window !== 'undefined' ? window.location.href : undefined,
                mainOrderId
            );

            console.log('[usePreviewOffer] Checkout session created (async):', result);

            return {
                checkoutToken: result.checkoutToken,
                customerId: result.customerId,
                status: result.status,
            };
        } catch (error) {
            console.error('[usePreviewOffer] Checkout creation failed:', error);
            throw error;
        } finally {
            setIsPaying(false);
        }
    }, [offerId, effectiveCurrency, lineItemsForPreview, offersResource, isPaying]);

    return {
        offer,
        isLoading,
        isFetching,
        isPaying,
        error: error as Error | null,
        summary,
        selections,
        // Precise methods
        selectVariant,
        updateQuantity,
        // Simple methods
        selectVariantByProduct,
        updateQuantityByProduct,
        // Helpers
        getAvailableVariants,
        // Payment
        pay,
        toCheckout,
    };
}
